import { apiGet, apiPost, apiClient } from './client.js';

export interface PricingMatrixModel {
  id: string;
  name: string;
  makeId: string;
  makeName: string;
}

export interface PricingMatrixService {
  id: string;
  name: string;
  categorySlug: string | null;
}

export interface PricingMatrixCell {
  modelId: string;
  serviceId: string;
  pricingRowId: string | null;
  price: string | null;
  warrantyDays: number | null;
  estimatedMinutes: number | null;
  active: boolean;
}

export interface PricingMatrixData {
  models: PricingMatrixModel[];
  services: PricingMatrixService[];
  pricing: PricingMatrixCell[];
  stats: {
    totalCells: number;
    missingPrices: number;
    zeroPrices: number;
  };
}

export interface PricingMatrixParams {
  category: string;
  makeId?: string;
  modelId?: string;
  serviceIds?: string;
}

export interface BulkPricingChange {
  type: 'update' | 'create';
  pricingRowId?: string;
  serviceId: string;
  modelId: string;
  makeId?: string;
  price: number;
  warrantyDays?: number;
  estimatedMinutes?: number;
}

export interface PricingImportPreview {
  preview: Array<{
    modelName: string;
    serviceName: string;
    modelId: string | null;
    serviceId: string | null;
    makeId: string | null;
    oldPrice: string | null;
    newPrice: string;
    action: 'update' | 'create' | 'skip';
    pricingRowId: string | null;
  }>;
  unmatchedModels: string[];
  unmatchedServices: string[];
}

export function getPricingMatrix(params: PricingMatrixParams) {
  return apiGet<PricingMatrixData>('/api/v1/repair-services/pricing-matrix', { params }).then(
    (res) => res.data,
  );
}

export function bulkUpdatePricing(changes: BulkPricingChange[]) {
  return apiPost<
    { updated: number; created: number; errors: Array<{ changeIndex: number; reason: string }> },
    { changes: BulkPricingChange[] }
  >('/api/v1/repair-services/pricing/bulk-update', { changes }).then((res) => res.data);
}

export function importPricingMatrixCsv(csv: string) {
  return apiPost<PricingImportPreview, { csv: string }>(
    '/api/v1/repair-services/pricing-matrix/import',
    { csv },
  ).then((res) => res.data);
}

export async function exportPricingMatrixCsv(params: PricingMatrixParams): Promise<Blob> {
  const token = (await import('@/stores/auth.js')).useAuthStore.getState().token;
  const search = new URLSearchParams();
  search.set('category', params.category);
  if (params.makeId) search.set('makeId', params.makeId);
  if (params.modelId) search.set('modelId', params.modelId);
  if (params.serviceIds) search.set('serviceIds', params.serviceIds);

  const base = apiClient.defaults.baseURL ?? '';
  const res = await fetch(`${base}/api/v1/repair-services/pricing-matrix/export?${search}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
}
